import type { ReferencedAppSettings } from "@certa/studio-core";
import type { AppSettings } from "@certa/queries/types/appSettings.types";
import { emptyReferencedAppSettings } from "../hooks/dbOpsSlices/appsettings.slice";
import { studioState } from "../hooks/useStudioAPI";

/**
 * Helper function to merge arrays based on unique identifier.
 * Gives preference to items from the referenced array over base array.
 */
export const mergeArraysByUniqueId = <T extends Record<string, any>>(
  baseArray: T[],
  referencedArray: T[] = [],
  uniqueIdKey: keyof T
): T[] => {
  const referencedMap = new Map(
    referencedArray.map(item => [item[uniqueIdKey], item])
  );

  // Map base items, replacing with referenced if exists
  const mappedBaseItems = baseArray.map(item =>
    referencedMap.has(item[uniqueIdKey])
      ? referencedMap.get(item[uniqueIdKey])!
      : item
  );

  // Add any referenced items that don't exist in base array
  const additionalReferencedItems = referencedArray.filter(
    item =>
      !baseArray.some(baseItem => baseItem[uniqueIdKey] === item[uniqueIdKey])
  );

  return [...mappedBaseItems, ...additionalReferencedItems];
};

/** Merges base app settings with referenced app settings from zustand slices.
 * Gives preference to referenced app settings over incoming base settings. */
export const mergeAppSettingsWithReferenced = (
  baseAppSettings: AppSettings = {
    ...emptyReferencedAppSettings,
    integrationConfig: [],
    integrationToken: []
  },
  referencedAppSettings: ReferencedAppSettings
): AppSettings => {
  return {
    // Email templates: unique by 'slug'
    emailTemplate: mergeArraysByUniqueId(
      baseAppSettings.emailTemplate,
      referencedAppSettings.emailTemplate,
      "slug"
    ),

    // Email attachments: unique by 'title'
    emailAttachment: mergeArraysByUniqueId(
      baseAppSettings.emailAttachment,
      referencedAppSettings.emailAttachment,
      "title"
    ),

    // Business units: unique by 'code'
    businessUnit: mergeArraysByUniqueId(
      baseAppSettings.businessUnit,
      referencedAppSettings.businessUnit,
      "code"
    ),

    // Regions: unique by 'code'
    region: mergeArraysByUniqueId(
      baseAppSettings.region,
      referencedAppSettings.region,
      "code"
    ),

    // Extra JSON: unique by 'tag'
    extraJSON: mergeArraysByUniqueId(
      baseAppSettings.extraJSON,
      referencedAppSettings.extraJSON,
      "tag"
    ),

    // Process kinds: unique by 'tag'
    processKind: mergeArraysByUniqueId(
      baseAppSettings.processKind,
      referencedAppSettings.processKind,
      "tag"
    ),

    // Process statuses: unique by 'tag'
    processStatus: mergeArraysByUniqueId(
      baseAppSettings.processStatus,
      referencedAppSettings.processStatus,
      "tag"
    ),

    // Object statuses: unique by 'tag'
    objectStatus: mergeArraysByUniqueId(
      baseAppSettings.objectStatus,
      referencedAppSettings.objectStatus,
      "tag"
    ),

    // Process PDF templates: unique by 'tag'
    processPDFTemplate: mergeArraysByUniqueId(
      baseAppSettings.processPDFTemplate,
      referencedAppSettings.processPDFTemplate,
      "tag"
    ),

    // Alert categories: unique by 'tag'
    alertCategory: mergeArraysByUniqueId(
      baseAppSettings.alertCategory,
      referencedAppSettings.alertCategory,
      "tag"
    ),

    // Comment flag options: unique by 'tag'
    commentFlagOption: mergeArraysByUniqueId(
      baseAppSettings.commentFlagOption,
      referencedAppSettings.commentFlagOption,
      "tag"
    ),

    // User groups: unique by 'name'
    group: mergeArraysByUniqueId(
      baseAppSettings.group,
      referencedAppSettings.group,
      "name"
    ),

    // Object type workflow kind relations: unique by composite key (objectType-workflowKind-relation)
    objectTypeWorkflowKindRelation: (() => {
      const referencedMap = new Map(
        referencedAppSettings.objectTypeWorkflowKindRelation.map(item => [
          `${item.objectType}-${item.workflowKind}-${item.relation}`,
          item
        ])
      );

      const mappedBaseItems =
        baseAppSettings.objectTypeWorkflowKindRelation.map(item => {
          const key = `${item.objectType}-${item.workflowKind}-${item.relation}`;
          return referencedMap.has(key) ? referencedMap.get(key)! : item;
        });

      const additionalReferencedItems =
        referencedAppSettings.objectTypeWorkflowKindRelation.filter(item => {
          const key = `${item.objectType}-${item.workflowKind}-${item.relation}`;
          return !baseAppSettings.objectTypeWorkflowKindRelation.some(
            baseItem =>
              `${baseItem.objectType}-${baseItem.workflowKind}-${baseItem.relation}` ===
              key
          );
        });

      return [...mappedBaseItems, ...additionalReferencedItems];
    })(),

    // Integration configs: unique by 'apiType'
    integrationConfig: mergeArraysByUniqueId(
      baseAppSettings.integrationConfig || [],
      referencedAppSettings.integrationConfig || [],
      "apiType"
    ),

    // Integration tokens: unique by 'tokenType'
    integrationToken: mergeArraysByUniqueId(
      baseAppSettings.integrationToken || [],
      referencedAppSettings.integrationToken || [],
      "tokenType"
    ),

    // Frontend extra metadata: simple override (keep from base)
    feExtraMetaData: baseAppSettings.feExtraMetaData
  };
};

export const mergeWithCurrentReferencedAppSettings = (
  incomingAppSettings: AppSettings
): AppSettings => {
  const referencedAppSettings = studioState().getAppSettings();

  return mergeAppSettingsWithReferenced(
    incomingAppSettings,
    referencedAppSettings
  );
};
